// src/components/profile/forms/HeroFormFields.jsx
import React, { useState, useEffect } from 'react';
import { 
  Camera, Settings, User, Building, Briefcase, Package, Plus, Trash2, Download,
  Eye, EyeOff, Palette, Type, BarChart3, Users, Trophy, Mail, Share2, Globe,
  Star, MessageCircle, Calendar, Award, Upload, Target
} from 'lucide-react';
import { FileUpload } from '../../common/FileUpload';
import { profileService } from '../../../services/profileService';

// Profile type statistics configuration
const PROFILE_TYPE_STATS = {
  personal: [
    { key: 'experience', label: 'Years Experience', icon: Briefcase },
    { key: 'projects', label: 'Projects Completed', icon: Package },
    { key: 'skills', label: 'Skills Mastered', icon: Star },
    { key: 'achievements', label: 'Achievements', icon: Trophy },
    { key: 'connections', label: 'Professional Connections', icon: Users },
    { key: 'education', label: 'Education Level', icon: Award }
  ],
  organization: [
    { key: 'founded', label: 'Years in Business', icon: Calendar },
    { key: 'employees', label: 'Team Members', icon: Users },
    { key: 'clients', label: 'Happy Clients', icon: MessageCircle },
    { key: 'projects', label: 'Projects Delivered', icon: Package },
    { key: 'awards', label: 'Awards Won', icon: Trophy },
    { key: 'locations', label: 'Office Locations', icon: Building }
  ],
  product: [
    { key: 'users', label: 'Active Users', icon: Users },
    { key: 'downloads', label: 'Downloads', icon: Package },
    { key: 'rating', label: 'User Rating', icon: Star },
    { key: 'reviews', label: 'Reviews', icon: MessageCircle },
    { key: 'features', label: 'Features', icon: Settings },
    { key: 'integrations', label: 'Integrations', icon: Building }
  ],
  service: [
    { key: 'clients', label: 'Clients Served', icon: Users },
    { key: 'projects', label: 'Projects Completed', icon: Package },
    { key: 'satisfaction', label: 'Client Satisfaction', icon: Star },
    { key: 'testimonials', label: 'Testimonials', icon: MessageCircle },
    { key: 'certifications', label: 'Certifications', icon: Award },
    { key: 'experience', label: 'Years Experience', icon: Calendar }
  ],
  project: [
    { key: 'team', label: 'Team Size', icon: Users },
    { key: 'duration', label: 'Project Duration', icon: Calendar },
    { key: 'budget', label: 'Budget Range', icon: Package },
    { key: 'technologies', label: 'Technologies Used', icon: Settings },
    { key: 'milestones', label: 'Milestones', icon: Trophy },
    { key: 'stakeholders', label: 'Stakeholders', icon: Building }
  ]
};

// HeroFormFields Component - ENHANCED VERSION
const HeroFormFields = ({ formData, updateFormData, profileType, bookId }) => {
  const [uploadStates, setUploadStates] = useState({
    heroImage: false,
    profileImage: false
  });
  const [uploadErrors, setUploadErrors] = useState({});

  useEffect(() => {
    if (!formData.standardButtons) {
      updateFormData('standardButtons', {
        testimonial: { enabled: true, text: 'Share Experience' },
        reviews: { enabled: true, text: 'Reviews', showBadge: true },
        contact: { enabled: true, text: 'Get In Touch' },
        share: { enabled: true, text: 'Share Profile' },
        download: { 
          enabled: true, 
          text: profileType === 'personal' ? 'Download CV' : 
                profileType === 'product' ? 'Download Specs' :
                profileType === 'service' ? 'Download Brochure' : 'Download Info'
        }
      });
    }
  }, [profileType]);

  // Upload handlers (preserved)
  const handleHeroImageUpload = async (file, error) => {
    if (error) {
      setUploadErrors(prev => ({ ...prev, heroImage: error }));
      return;
    }

    if (!file) {
      updateFormData('heroImageUrl', '');
      return;
    }

    setUploadStates(prev => ({ ...prev, heroImage: true }));
    setUploadErrors(prev => ({ ...prev, heroImage: '' }));

    try {
      const result = await profileService.uploadProfileMedia(file, bookId, 'hero', 'hero-background');
      updateFormData('heroImageUrl', result.url);
    } catch (error) {
      setUploadErrors(prev => ({ ...prev, heroImage: error.message }));
    } finally {
      setUploadStates(prev => ({ ...prev, heroImage: false }));
    }
  };

  const handleProfileImageUpload = async (file, error) => {
    if (error) {
      setUploadErrors(prev => ({ ...prev, profileImage: error }));
      return;
    }

    if (!file) {
      updateFormData('profileImageUrl', '');
      return;
    }

    setUploadStates(prev => ({ ...prev, profileImage: true }));
    setUploadErrors(prev => ({ ...prev, profileImage: '' }));

    try {
      const result = await profileService.uploadProfileMedia(file, bookId, 'hero', 'profile-image');
      updateFormData('profileImageUrl', result.url);
    } catch (error) {
      setUploadErrors(prev => ({ ...prev, profileImage: error.message }));
    } finally {
      setUploadStates(prev => ({ ...prev, profileImage: false }));
    }
  };

  // Action button handlers
  const addActionButton = () => {
    const currentButtons = formData.actionButtons || [];
    updateFormData('actionButtons', [
      ...currentButtons,
      { 
        text: '', 
        action: 'contact', 
        style: 'primary',
        url: '',
        icon: 'MessageCircle'
      }
    ]);
  };

  const updateStandardButton = (buttonKey, field, value) => {
    updateFormData('standardButtons', {
      ...formData.standardButtons,
      [buttonKey]: {
        ...formData.standardButtons?.[buttonKey],
        [field]: value
      }
    });
  };

  // Social link handlers
  const addSocialLink = () => {
    const currentLinks = formData.socialLinks || [];
    updateFormData('socialLinks', [
      ...currentLinks,
      { platform: 'linkedin', url: '' }
    ]);
  };

  const updateSocialLink = (index, field, value) => {
    const updatedLinks = [...(formData.socialLinks || [])];
    updatedLinks[index] = { ...updatedLinks[index], [field]: value };
    updateFormData('socialLinks', updatedLinks);
  };

  const removeSocialLink = (index) => {
    const updatedLinks = (formData.socialLinks || []).filter((_, i) => i !== index);
    updateFormData('socialLinks', updatedLinks);
  };

  // Custom button handlers
  const addCustomButton = () => {
    const currentButtons = formData.customButtons || [];
    updateFormData('customButtons', [
      ...currentButtons,
      { 
        text: '', 
        action: 'contact', 
        style: 'primary',
        url: '',
        icon: 'MessageCircle'
      }
    ]);
  };

  const updateCustomButton = (index, field, value) => {
    const updatedButtons = [...(formData.customButtons || [])];
    updatedButtons[index] = { ...updatedButtons[index], [field]: value };
    updateFormData('customButtons', updatedButtons);
  };

  const removeCustomButton = (index) => {
    const updatedButtons = (formData.customButtons || []).filter((_, i) => i !== index);
    updateFormData('customButtons', updatedButtons);
  };

  const updateActionButton = (index, field, value) => {
    const updatedButtons = [...(formData.actionButtons || [])];
    updatedButtons[index] = { ...updatedButtons[index], [field]: value };
    updateFormData('actionButtons', updatedButtons);
  };

  const removeActionButton = (index) => {
    const updatedButtons = (formData.actionButtons || []).filter((_, i) => i !== index);
    updateFormData('actionButtons', updatedButtons);
  };

  return (
    <div className="space-y-6">
      {/* Hero Images Section */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Camera className="h-5 w-5" />
          Hero Images
        </h4>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Hero Background Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Hero Background Image
            </label>
            <FileUpload
              label="Upload Hero Background"
              currentFile={formData.heroImageUrl}
              onFileSelect={handleHeroImageUpload}
              onRemove={() => updateFormData('heroImageUrl', '')}
              accept="image/*"
              maxSize={15}
              uploading={uploadStates.heroImage}
              error={uploadErrors.heroImage}
              showPreview={true}
            />
            <p className="text-xs text-gray-500 mt-2">
              Recommended: 1920x800px, JPG/PNG, max 15MB
            </p>
          </div>

          {/* Profile Image/Logo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {profileType === 'organization' ? 'Organization Logo' : 'Profile Image'}
            </label>
            <FileUpload
              label={`Upload ${profileType === 'organization' ? 'Logo' : 'Profile Image'}`}
              currentFile={formData.profileImageUrl}
              onFileSelect={handleProfileImageUpload}
              onRemove={() => updateFormData('profileImageUrl', '')}
              accept="image/*"
              maxSize={5}
              uploading={uploadStates.profileImage}
              error={uploadErrors.profileImage}
              showPreview={true}
            />
            <p className="text-xs text-gray-500 mt-2">
              {profileType === 'organization' ? 
                'Recommended: 400x400px, transparent PNG preferred' :
                'Recommended: 400x400px, JPG/PNG, max 5MB'
              }
            </p>
          </div>
        </div>
      </div>

      {/* NEW: Profile Image Dimensions & Position */}
      <div className="bg-purple-50 rounded-lg p-6 border border-purple-200">
        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Profile Image Dimensions & Position
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Image Size */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Image Size
            </label>
            <div className="space-y-3">
              {/* Width */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Width (px)</label>
                <input
                  type="range"
                  min="80"
                  max="300"
                  step="10"
                  value={formData.profileImageWidth || 160}
                  onChange={(e) => updateFormData('profileImageWidth', parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>80px</span>
                  <span className="font-medium text-purple-600">{formData.profileImageWidth || 160}px</span>
                  <span>300px</span>
                </div>
              </div>

              {/* Height */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Height (px)</label>
                <input
                  type="range"
                  min="80"
                  max="300"
                  step="10"
                  value={formData.profileImageHeight || 160}
                  onChange={(e) => updateFormData('profileImageHeight', parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>80px</span>
                  <span className="font-medium text-purple-600">{formData.profileImageHeight || 160}px</span>
                  <span>300px</span>
                </div>
              </div>

              {/* Aspect Ratio Lock */}
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={formData.lockAspectRatio !== false}
                  onChange={(e) => {
                    updateFormData('lockAspectRatio', e.target.checked);
                    if (e.target.checked) {
                      // Make height match width when locking
                      updateFormData('profileImageHeight', formData.profileImageWidth || 160);
                    }
                  }}
                  className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <span className="text-gray-700">Lock aspect ratio (1:1)</span>
              </label>
            </div>
          </div>

          {/* Position */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Image Position
            </label>
            <div className="space-y-3">
              {/* Horizontal Position */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">Horizontal Alignment</label>
                <div className="grid grid-cols-3 gap-2">
                  {['left', 'center', 'right'].map((pos) => (
                    <button
                      key={pos}
                      type="button"
                      onClick={() => updateFormData('profileImageHorizontal', pos)}
                      className={`px-3 py-2 text-sm rounded-lg border-2 transition-all ${
                        (formData.profileImageHorizontal || 'left') === pos
                          ? 'border-purple-500 bg-purple-50 text-purple-700 font-medium'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {pos.charAt(0).toUpperCase() + pos.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Vertical Position */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">Vertical Alignment</label>
                <div className="grid grid-cols-3 gap-2">
                  {['top', 'center', 'bottom'].map((pos) => (
                    <button
                      key={pos}
                      type="button"
                      onClick={() => updateFormData('profileImageVertical', pos)}
                      className={`px-3 py-2 text-sm rounded-lg border-2 transition-all ${
                        (formData.profileImageVertical || 'top') === pos
                          ? 'border-purple-500 bg-purple-50 text-purple-700 font-medium'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {pos.charAt(0).toUpperCase() + pos.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Object Fit */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">Image Fit</label>
                <select
                  value={formData.profileImageFit || 'cover'}
                  onChange={(e) => updateFormData('profileImageFit', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="cover">Cover (fill container)</option>
                  <option value="contain">Contain (fit inside)</option>
                  <option value="fill">Fill (stretch to fit)</option>
                  <option value="scale-down">Scale Down (smaller only)</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Live Preview */}
        <div className="mt-6 p-6 bg-white rounded-lg border border-gray-200">
          <label className="block text-sm font-medium text-gray-700 mb-3">Position Preview</label>
          <div 
            className="relative bg-gradient-to-r from-purple-100 to-blue-100 rounded-lg overflow-hidden"
            style={{ height: '200px' }}
          >
            <div 
              className="absolute rounded-full overflow-hidden border-4 border-white shadow-lg bg-white"
              style={{
                width: `${formData.profileImageWidth || 160}px`,
                height: `${formData.profileImageHeight || 160}px`,
                left: formData.profileImageHorizontal === 'center' ? '50%' : 
                      formData.profileImageHorizontal === 'right' ? 'auto' : '20px',
                right: formData.profileImageHorizontal === 'right' ? '20px' : 'auto',
                top: formData.profileImageVertical === 'center' ? '50%' : 
                     formData.profileImageVertical === 'bottom' ? 'auto' : '20px',
                bottom: formData.profileImageVertical === 'bottom' ? '20px' : 'auto',
                transform: `${formData.profileImageHorizontal === 'center' ? 'translateX(-50%)' : ''} ${formData.profileImageVertical === 'center' ? 'translateY(-50%)' : ''}`.trim()
              }}
            >
              {formData.profileImageUrl ? (
                <img 
                  src={formData.profileImageUrl} 
                  alt="Preview" 
                  className="w-full h-full"
                  style={{ objectFit: formData.profileImageFit || 'cover' }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  <User className="w-12 h-12" />
                </div>
              )}
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2 text-center">
            Preview shows approximate positioning in hero section
          </p>
        </div>
      </div>

      {/* NEW: Visual Customization (Overlay, Border, Background) */}
      <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Visual Customization
        </h4>

        {/* TOGGLE SWITCHES FOR ENABLING FEATURES */}
        <div className="space-y-4 mb-6 pb-6 border-b border-blue-200">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.enableOverlay !== false}
              onChange={(e) => updateFormData('enableOverlay', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
            />
            <div>
              <span className="text-sm font-medium text-gray-900">Enable Background Overlay</span>
              <p className="text-xs text-gray-500">Add a dark overlay to improve text readability on background images</p>
            </div>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.enableProfileBorder !== false}
              onChange={(e) => updateFormData('enableProfileBorder', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
            />
            <div>
              <span className="text-sm font-medium text-gray-900">Enable Profile Image Border</span>
              <p className="text-xs text-gray-500">Add a customizable border around your profile image</p>
            </div>
          </label>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.enableProfileBackground !== false}
              onChange={(e) => updateFormData('enableProfileBackground', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
            />
            <div>
              <span className="text-sm font-medium text-gray-900">Enable Profile Image Background</span>
              <p className="text-xs text-gray-500">Add a background color or effect behind your profile image</p>
            </div>
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Background Overlay Opacity - Only show if enabled */}
          {formData.enableOverlay !== false && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Background Overlay Opacity
              </label>
              <div className="space-y-3">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={formData.overlayOpacity || 0.5}
                  onChange={(e) => updateFormData('overlayOpacity', parseFloat(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Transparent (0%)</span>
                  <span className="font-medium text-blue-600">
                    {Math.round((formData.overlayOpacity || 0.5) * 100)}%
                  </span>
                  <span>Opaque (100%)</span>
                </div>
                <p className="text-xs text-gray-500">
                  Controls the darkness of the overlay on your background image. Higher values make text more readable.
                </p>
              </div>
            </div>
          )}

          {/* Profile Image Border - Only show if enabled */}
          {formData.enableProfileBorder !== false && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Profile Image Border
              </label>
              <div className="space-y-3">
                {/* Border Color */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Border Color</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={formData.profileImageBorder?.color || '#ffffff'}
                      onChange={(e) => updateFormData('profileImageBorder', {
                        ...formData.profileImageBorder,
                        color: e.target.value
                      })}
                      className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                    />
                    <input
                      type="text"
                      value={formData.profileImageBorder?.color || '#ffffff'}
                      onChange={(e) => updateFormData('profileImageBorder', {
                        ...formData.profileImageBorder,
                        color: e.target.value
                      })}
                      className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="#ffffff"
                    />
                  </div>
                </div>

                {/* Border Width */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Border Width</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="range"
                      min="0"
                      max="12"
                      step="1"
                      value={formData.profileImageBorder?.width || 4}
                      onChange={(e) => updateFormData('profileImageBorder', {
                        ...formData.profileImageBorder,
                        width: parseInt(e.target.value)
                      })}
                      className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <span className="text-sm font-medium text-gray-700 min-w-[3rem]">
                      {formData.profileImageBorder?.width || 4}px
                    </span>
                  </div>
                </div>

                {/* Border Style */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Border Style</label>
                  <select
                    value={formData.profileImageBorder?.style || 'solid'}
                    onChange={(e) => updateFormData('profileImageBorder', {
                      ...formData.profileImageBorder,
                      style: e.target.value
                    })}
                    className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="solid">Solid</option>
                    <option value="dashed">Dashed</option>
                    <option value="dotted">Dotted</option>
                    <option value="double">Double</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Profile Image Background - Only show if enabled */}
        {formData.enableProfileBackground !== false && (
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Profile Image Background
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Background Type */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2">Background Type</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="profileBgType"
                      value="solid"
                      checked={(formData.profileImageBackgroundType || 'solid') === 'solid'}
                      onChange={(e) => updateFormData('profileImageBackgroundType', e.target.value)}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">Solid Color</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="profileBgType"
                      value="gradient"
                      checked={formData.profileImageBackgroundType === 'gradient'}
                      onChange={(e) => updateFormData('profileImageBackgroundType', e.target.value)}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">Gradient</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="profileBgType"
                      value="blur"
                      checked={formData.profileImageBackgroundType === 'blur'}
                      onChange={(e) => updateFormData('profileImageBackgroundType', e.target.value)}
                      className="text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">Glassmorphism (Blur)</span>
                  </label>
                </div>
              </div>

              {/* Background Settings */}
              <div>
                {(formData.profileImageBackgroundType || 'solid') === 'solid' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Background Color</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.profileImageBackground?.includes('rgba') ? 
                          '#ffffff' : (formData.profileImageBackground || '#ffffff')}
                        onChange={(e) => updateFormData('profileImageBackground', e.target.value)}
                        className="w-12 h-8 border border-gray-300 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.profileImageBackground || 'rgba(255, 255, 255, 0.1)'}
                        onChange={(e) => updateFormData('profileImageBackground', e.target.value)}
                        className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="rgba(255, 255, 255, 0.1)"
                      />
                    </div>
                  </div>
                )}

                {formData.profileImageBackgroundType === 'gradient' && (
                  <div className="space-y-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Gradient Start</label>
                      <input
                        type="color"
                        value={formData.profileImageGradient?.start || '#6366f1'}
                        onChange={(e) => updateFormData('profileImageGradient', {
                          ...formData.profileImageGradient,
                          start: e.target.value
                        })}
                        className="w-full h-8 border border-gray-300 rounded cursor-pointer"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Gradient End</label>
                      <input
                        type="color"
                        value={formData.profileImageGradient?.end || '#8b5cf6'}
                        onChange={(e) => updateFormData('profileImageGradient', {
                          ...formData.profileImageGradient,
                          end: e.target.value
                        })}
                        className="w-full h-8 border border-gray-300 rounded cursor-pointer"
                      />
                    </div>
                  </div>
                )}

                {formData.profileImageBackgroundType === 'blur' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Blur Intensity</label>
                    <input
                      type="range"
                      min="4"
                      max="20"
                      step="2"
                      value={formData.profileImageBlur || 8}
                      onChange={(e) => updateFormData('profileImageBlur', parseInt(e.target.value))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>Light (4px)</span>
                      <span className="font-medium text-blue-600">{formData.profileImageBlur || 8}px</span>
                      <span>Heavy (20px)</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

       {/* UPDATED: Standard Action Buttons Configuration */}
      <div className="bg-green-50 rounded-lg p-6 border border-green-200">
        <div className="mb-6">
          <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-2">
            <Settings className="h-5 w-5" />
            Hero Action Buttons
          </h4>
          <p className="text-sm text-gray-600">Configure which buttons appear in your hero section</p>
        </div>

        <div className="space-y-6">
          {/* Testimonial Button */}
          <div className="p-4 bg-white border-2 border-gray-200 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.standardButtons?.testimonial?.enabled !== false}
                  onChange={(e) => updateStandardButton('testimonial', 'enabled', e.target.checked)}
                  className="rounded border-gray-300 text-green-600 focus:ring-green-500 h-4 w-4"
                />
                <div className="flex items-center gap-2">
                  <Plus className="h-5 w-5 text-green-500" />
                  <span className="font-medium text-gray-900">Share Experience Button</span>
                </div>
              </label>
            </div>

            {formData.standardButtons?.testimonial?.enabled !== false && (
              <div className="ml-7 space-y-3 pt-3 border-t border-gray-200">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Button Text</label>
                  <input
                    type="text"
                    value={formData.standardButtons?.testimonial?.text || 'Share Experience'}
                    onChange={(e) => updateStandardButton('testimonial', 'text', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Share Experience"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Allows visitors to submit testimonials about your work
                </p>
              </div>
            )}
          </div>

          {/* Reviews Button */}
          <div className="p-4 bg-white border-2 border-gray-200 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.standardButtons?.reviews?.enabled !== false}
                  onChange={(e) => updateStandardButton('reviews', 'enabled', e.target.checked)}
                  className="rounded border-gray-300 text-green-600 focus:ring-green-500 h-4 w-4"
                />
                <div className="flex items-center gap-2">
                  <MessageCircle className="h-5 w-5 text-blue-500" />
                  <span className="font-medium text-gray-900">Reviews Button</span>
                </div>
              </label>
            </div>

            {formData.standardButtons?.reviews?.enabled !== false && (
              <div className="ml-7 space-y-3 pt-3 border-t border-gray-200">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Button Text</label>
                  <input
                    type="text"
                    value={formData.standardButtons?.reviews?.text || 'Reviews'}
                    onChange={(e) => updateStandardButton('reviews', 'text', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Reviews"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={formData.standardButtons?.reviews?.showBadge !== false}
                    onChange={(e) => updateStandardButton('reviews', 'showBadge', e.target.checked)}
                    className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  <span className="text-gray-700">Show testimonial count badge</span>
                </label>
                <p className="text-xs text-gray-500">
                  Opens modal to view all public testimonials
                </p>
              </div>
            )}
          </div>

          {/* Contact Button */}
          <div className="p-4 bg-white border-2 border-gray-200 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.standardButtons?.contact?.enabled !== false}
                  onChange={(e) => updateStandardButton('contact', 'enabled', e.target.checked)}
                  className="rounded border-gray-300 text-green-600 focus:ring-green-500 h-4 w-4"
                />
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-purple-500" />
                  <span className="font-medium text-gray-900">Get In Touch Button</span>
                </div>
              </label>
            </div>

            {formData.standardButtons?.contact?.enabled !== false && (
              <div className="ml-7 space-y-3 pt-3 border-t border-gray-200">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Button Text</label>
                  <input
                    type="text"
                    value={formData.standardButtons?.contact?.text || 'Get In Touch'}
                    onChange={(e) => updateStandardButton('contact', 'text', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Get In Touch"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Scrolls to the contact section
                </p>
              </div>
            )}
          </div>

          {/* Share Button */}
          <div className="p-4 bg-white border-2 border-gray-200 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.standardButtons?.share?.enabled !== false}
                  onChange={(e) => updateStandardButton('share', 'enabled', e.target.checked)}
                  className="rounded border-gray-300 text-green-600 focus:ring-green-500 h-4 w-4"
                />
                <div className="flex items-center gap-2">
                  <Share2 className="h-5 w-5 text-indigo-500" />
                  <span className="font-medium text-gray-900">Share Profile Button</span>
                </div>
              </label>
            </div>

            {formData.standardButtons?.share?.enabled !== false && (
              <div className="ml-7 space-y-3 pt-3 border-t border-gray-200">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Button Text</label>
                  <input
                    type="text"
                    value={formData.standardButtons?.share?.text || 'Share Profile'}
                    onChange={(e) => updateStandardButton('share', 'text', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Share Profile"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Opens share modal with social sharing options
                </p>
              </div>
            )}
          </div>

          {/* Download Button */}
          <div className="p-4 bg-white border-2 border-gray-200 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.standardButtons?.download?.enabled !== false}
                  onChange={(e) => updateStandardButton('download', 'enabled', e.target.checked)}
                  className="rounded border-gray-300 text-green-600 focus:ring-green-500 h-4 w-4"
                />
                <div className="flex items-center gap-2">
                  <Download className="h-5 w-5 text-cyan-500" />
                  <span className="font-medium text-gray-900">Download Button</span>
                </div>
              </label>
            </div>

            {formData.standardButtons?.download?.enabled !== false && (
              <div className="ml-7 space-y-3 pt-3 border-t border-gray-200">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Button Text</label>
                  <input
                    type="text"
                    value={formData.standardButtons?.download?.text || (
                      profileType === 'personal' ? 'Download CV' : 
                      profileType === 'product' ? 'Download Specs' :
                      profileType === 'service' ? 'Download Brochure' : 'Download Info'
                    )}
                    onChange={(e) => updateStandardButton('download', 'text', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Download CV"
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Triggers download action (CV, specs, brochure, etc.)
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Social Links Configuration */}
      <div className="bg-sky-50 rounded-lg p-6 border border-sky-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Globe className="h-5 w-5" />
              Social Links
            </h4>
            <p className="text-sm text-gray-600 mt-1">Add social media links to your hero section</p>
          </div>
          <button
            type="button"
            onClick={addSocialLink}
            className="flex items-center gap-2 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors text-sm font-medium"
          >
            <Plus className="h-4 w-4" />
            Add Link
          </button>
        </div>

        {/* Show Social Links Toggle */}
        <label className="flex items-center gap-3 mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.showSocialLinks !== false}
            onChange={(e) => updateFormData('showSocialLinks', e.target.checked)}
            className="rounded border-gray-300 text-sky-600 focus:ring-sky-500 h-4 w-4"
          />
          <span className="text-sm font-medium text-gray-700">Show social links in hero section</span>
        </label>

        {formData.showSocialLinks !== false && (
          <div className="space-y-4">
            {(!formData.socialLinks || formData.socialLinks.length === 0) && (
              <div className="text-center py-8 text-gray-500 bg-white rounded-lg border-2 border-dashed border-gray-300">
                <p className="text-sm">No social links yet. Click "Add Link" to create one.</p>
              </div>
            )}

            {(formData.socialLinks || []).map((link, index) => (
              <div key={index} className="p-4 bg-white border-2 border-gray-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-gray-700">Social Link {index + 1}</span>
                  <button
                    type="button"
                    onClick={() => removeSocialLink(index)}
                    className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Platform */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Platform</label>
                    <select
                      value={link.platform}
                      onChange={(e) => updateSocialLink(index, 'platform', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                    >
                      <option value="linkedin">LinkedIn</option>
                      <option value="github">GitHub</option>
                      <option value="twitter">Twitter</option>
                      <option value="facebook">Facebook</option>
                      <option value="instagram">Instagram</option>
                      <option value="youtube">YouTube</option>
                      <option value="website">Website</option>
                    </select>
                  </div>

                  {/* URL */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">URL</label>
                    <input
                      type="url"
                      value={link.url}
                      onChange={(e) => updateSocialLink(index, 'url', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500"
                      placeholder="https://linkedin.com/in/username"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Custom Action Buttons */}
      <div className="bg-amber-50 rounded-lg p-6 border border-amber-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Custom Action Buttons
            </h4>
            <p className="text-sm text-gray-600 mt-1">Add additional custom buttons with specific actions</p>
          </div>
          <button
            type="button"
            onClick={addCustomButton}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium"
          >
            <Plus className="h-4 w-4" />
            Add Button
          </button>
        </div>

        <div className="space-y-4">
          {(!formData.customButtons || formData.customButtons.length === 0) && (
            <div className="text-center py-8 text-gray-500 bg-white rounded-lg border-2 border-dashed border-gray-300">
              <p className="text-sm">No custom buttons yet. Click "Add Button" to create one.</p>
            </div>
          )}

          {(formData.customButtons || []).map((button, index) => (
            <div key={index} className="p-4 bg-white border-2 border-gray-200 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Custom Button {index + 1}</span>
                <button
                  type="button"
                  onClick={() => removeCustomButton(index)}
                  className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Button Text */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Button Text</label>
                  <input
                    type="text"
                    value={button.text}
                    onChange={(e) => updateCustomButton(index, 'text', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                    placeholder="e.g., Book Now, View Portfolio"
                  />
                </div>

                {/* Action */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Action</label>
                  <select
                    value={button.action}
                    onChange={(e) => updateCustomButton(index, 'action', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="contact">Scroll to Contact</option>
                    <option value="about">Scroll to About</option>
                    <option value="projects">Scroll to Projects</option>
                    <option value="services">Scroll to Services</option>
                    <option value="testimonials">Scroll to Testimonials</option>
                    <option value="custom">Custom URL</option>
                  </select>
                </div>

                {/* Custom URL */}
                {button.action === 'custom' && (
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Custom URL</label>
                    <input
                      type="url"
                      value={button.url || ''}
                      onChange={(e) => updateCustomButton(index, 'url', e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                      placeholder="https://example.com or /path"
                    />
                  </div>
                )}

                {/* Style */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Style</label>
                  <select
                    value={button.style}
                    onChange={(e) => updateCustomButton(index, 'style', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="filled">Filled (White)</option>
                    <option value="outlined">Outlined (Border)</option>
                    <option value="transparent">Transparent</option>
                  </select>
                </div>

                {/* Icon */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Icon</label>
                  <select
                    value={button.icon || 'none'}
                    onChange={(e) => updateCustomButton(index, 'icon', e.target.value === 'none' ? '' : e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="none">No Icon</option>
                    <option value="Calendar">Calendar</option>
                    <option value="Star">Star</option>
                    <option value="Award">Award</option>
                    <option value="FileText">Document</option>
                    <option value="Play">Play</option>
                  </select>
                </div>
              </div>

              {/* Preview */}
              <div className="pt-3 border-t border-gray-200">
                <label className="block text-xs font-medium text-gray-600 mb-2">Preview</label>
                <button
                  type="button"
                  className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                    button.style === 'filled' 
                      ? 'bg-white text-gray-900'
                      : button.style === 'outlined'
                      ? 'border-2 border-white text-white bg-transparent'
                      : 'text-white bg-transparent border-2 border-transparent'
                  }`}
                >
                  {button.icon && button.icon !== 'none' && (
                    <span>📌</span>
                  )}
                  {button.text || 'Button Text'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Hero Content Fields (PRESERVED) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {profileType === 'organization' ? 'Organization Name' : 'Full Name'}
          </label>
          <input
            type="text"
            value={formData.name || ''}
            onChange={(e) => updateFormData('name', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder={profileType === 'organization' ? 
              'Enter organization name' : 
              'Enter your full name'
            }
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {profileType === 'organization' ? 'Organization Type' : 'Professional Title'}
          </label>
          <input
            type="text"
            value={formData.title || ''}
            onChange={(e) => updateFormData('title', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder={profileType === 'organization' ? 
              'e.g., Technology Company' : 
              'e.g., Software Engineer'
            }
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tagline
          </label>
          <input
            type="text"
            value={formData.tagline || ''}
            onChange={(e) => updateFormData('tagline', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder={profileType === 'organization' ? 
              'e.g., Innovating for a Better Tomorrow' : 
              'e.g., Building Amazing Digital Experiences'
            }
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Value Proposition / Description
          </label>
          <textarea
            value={formData.valueProposition || ''}
            onChange={(e) => updateFormData('valueProposition', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
            placeholder="Brief description of what you offer or what makes you unique..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Status Badge Text
          </label>
          <input
            type="text"
            value={formData.status || ''}
            onChange={(e) => updateFormData('status', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="e.g., Available for Work, Hiring"
          />
        </div>

        <div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.showContact !== false}
              onChange={(e) => updateFormData('showContact', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">Show Contact button in hero</span>
          </label>
        </div>
      </div>
    </div>
  );
};

const getValuePropositionPlaceholder = (profileType) => {
  const placeholders = {
    personal: "Turning complex problems into elegant solutions. Ready to bring innovation to your next project.",
    organization: "Transforming businesses through cutting-edge technology and strategic partnerships.",
    product: "The most intuitive solution for teams who value simplicity without compromising power.",
    service: "Delivering exceptional results that exceed expectations. Your success is our priority.",
    project: "Building the future of sustainable technology with measurable impact on communities."
  };
  
  return placeholders[profileType] || placeholders.personal;
};

export default HeroFormFields;